import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { 
  Scissors, 
  ArrowLeft,
  CheckCircle,
  Circle,
  ChevronRight,
  User,
  Calendar,
  CreditCard,
  Gift,
  Share2,
  MapPin,
  Star,
  Clock,
  Smartphone,
  DollarSign,
  Users,
  Crown,
  Play,
  Download,
  BookOpen
} from "lucide-react";
import GetStartedGuide from "@/components/GetStartedGuide";
import AppIntroduction from "@/components/AppIntroduction";

const OnboardingGuidePage = () => {
  const [expandedStep, setExpandedStep] = useState(1);
  const [showClientGuide, setShowClientGuide] = useState(false);
  const [showStylistGuide, setShowStylistGuide] = useState(false);
  const [showOwnerGuide, setShowOwnerGuide] = useState(false);
  const [showIntro, setShowIntro] = useState(false);

  const steps = [
    {
      number: 1,
      title: "Create Your Account",
      duration: "2 minutes",
      description: "Sign up and choose your professional role",
      details: [
        "Go to myhairchair.com and click 'Start for Free'",
        "Select 'Barber / Stylist' as your role",
        "Enter your name, email, and create a password",
        "Or sign up instantly with Google",
        "You'll be taken to choose your membership plan"
      ],
      tip: "Start with the Free plan to explore. You can upgrade to Pro ($29/mo) anytime to unlock loyalty rewards, calendar sync, and more!"
    },
    {
      number: 2,
      title: "Complete Your Profile",
      duration: "5 minutes",
      description: "Add your bio, specialties, and location",
      details: [
        "Click 'My Profile' in your dashboard sidebar",
        "Add a professional bio (tell clients what makes you special!)",
        "Select your specialties (Fades, Braids, Color, etc.)",
        "Choose if you're a Barber or Stylist",
        "Add your location (City, State) so clients can find you",
        "Set your years of experience"
      ],
      tip: "A complete profile gets 3x more bookings! Include your specialties and what you're passionate about."
    },
    {
      number: 3,
      title: "Set Up Your Services & Pricing",
      duration: "5 minutes",
      description: "Add the services you offer with prices",
      details: [
        "Go to 'Services & Pricing' tab in your dashboard",
        "Click 'Add from Templates' to quickly add common services",
        "Or click 'Add Custom Service' for unique offerings",
        "Set your price and duration for each service",
        "Edit anytime as your prices change"
      ],
      tip: "Be competitive but don't undersell yourself. Check what other pros in your area charge!"
    },
    {
      number: 4,
      title: "Set Your Availability",
      duration: "3 minutes",
      description: "Tell clients when you're available for bookings",
      details: [
        "Go to 'Availability' tab in your dashboard",
        "Select the days you work",
        "Set your start and end times for each day",
        "Block off lunch breaks or personal time",
        "Your calendar will only show available slots to clients"
      ],
      tip: "Keep your availability updated! Clients trust professionals who have accurate schedules."
    },
    {
      number: 5,
      title: "Connect Google Calendar (Pro)",
      duration: "2 minutes",
      description: "Sync appointments to your personal calendar",
      details: [
        "In 'My Profile', find the Google Calendar section",
        "Click 'Connect Google Calendar'",
        "Sign in with your Google account",
        "Grant permission to add events",
        "New bookings automatically appear in your Google Calendar!"
      ],
      tip: "This feature requires Pro membership ($29/mo). Never double-book again!"
    },
    {
      number: 6,
      title: "Set Up Payment Methods (Pro)",
      duration: "3 minutes",
      description: "Add PayPal, Cash App, Zelle for client payments",
      details: [
        "In 'My Profile', find 'Payment Methods' section",
        "Add your PayPal email or PayPal.me link",
        "Add your Cash App $cashtag",
        "Add your Zelle email or phone number",
        "Choose your preferred payment method",
        "Clients will see these options when booking"
      ],
      tip: "Offering multiple payment options increases bookings by 40%!"
    },
    {
      number: 7,
      title: "Share Your Booking Link",
      duration: "1 minute",
      description: "Get clients to book with you directly",
      details: [
        "Your unique booking link is: myhairchair.com/book?stylist=YOUR_ID",
        "Share this link on Instagram, Facebook, Twitter",
        "Add it to your bio on social media",
        "Text it to existing clients",
        "Clients can book 24/7 without calling you!"
      ],
      tip: "Use the Social Sharing feature (Pro) to create professional posts with your booking link!"
    },
    {
      number: 8,
      title: "Configure Loyalty Rewards (Pro)",
      duration: "3 minutes",
      description: "Set up rewards to keep clients coming back",
      details: [
        "Go to 'Rewards' tab in your dashboard",
        "Enable the loyalty program",
        "Set points per dollar spent (default: 1 point = $1)",
        "Create reward tiers (e.g., 100 points = $10 off)",
        "Bonus: Give new clients extra points for their first visit!",
        "Clients earn points automatically when appointments complete"
      ],
      tip: "Loyalty programs increase repeat bookings by 67%!"
    }
  ];

  const faqs = [
    {
      question: "How do I get paid?",
      answer: "You handle payments directly with clients using your preferred method (Cash, Card, PayPal, Cash App, Zelle). MyHairChair doesn't take a cut of your earnings - we only charge a flat monthly fee for Pro features."
    },
    {
      question: "What plans do you offer?",
      answer: "We offer two plans: Pro ($29.99/mo) for individual stylists with unlimited clients, CRM dashboard, Pro Mini-Site, and more. Elite ($49.99/mo) for salon owners includes everything in Pro plus staff management, rent collection, and team analytics. Both include a 30-day FREE trial!"
    },
    {
      question: "How do clients find me?",
      answer: "Clients can find you on the 'Find Stylists' page with our map view. Complete your profile with location, specialties, and a great bio to appear in search results!"
    },
    {
      question: "Can I use this on my phone?",
      answer: "Yes! MyHairChair is mobile-friendly. Access your dashboard, manage appointments, and check your schedule from any device."
    },
    {
      question: "What if a client cancels?",
      answer: "You'll receive an email notification when a client cancels. Consider setting up a cancellation policy and communicating it to clients."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-900 to-black py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/images/myhairchair-logo.png" 
              alt="MyHairChair" 
              className="h-10 sm:h-12 md:h-14 w-auto"
            />
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Stylist <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">Onboarding Guide</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Welcome! Follow these simple steps to set up your profile and start accepting bookings in under 30 minutes.
          </p>
        </div>

        {/* Interactive Guides Section */}
        <Card className="bg-gradient-to-br from-stone-800/80 to-stone-900/80 border-stone-700 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Play className="w-5 h-5 text-amber-400" />
              <h3 className="text-white font-semibold">Interactive Guides & Video</h3>
              <Badge className="bg-amber-500/20 text-amber-400">Quick Start</Badge>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Watch our video or launch an interactive walkthrough for your role.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Button
                onClick={() => setShowIntro(true)}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black hover:from-amber-600 hover:to-yellow-600 h-auto py-3 flex flex-col items-center gap-1"
              >
                <Play className="w-5 h-5" />
                <span className="text-sm font-semibold">How It Works</span>
                <span className="text-xs opacity-70">2 min video</span>
              </Button>
              <Button
                onClick={() => setShowClientGuide(true)}
                variant="outline"
                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 h-auto py-3 flex flex-col items-center gap-1"
              >
                <Users className="w-5 h-5" />
                <span className="text-sm font-semibold">Client Guide</span>
                <span className="text-xs opacity-70">5 steps</span>
              </Button>
              <Button
                onClick={() => setShowStylistGuide(true)}
                variant="outline"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 h-auto py-3 flex flex-col items-center gap-1"
              >
                <Scissors className="w-5 h-5" />
                <span className="text-sm font-semibold">Stylist Guide</span>
                <span className="text-xs opacity-70">8 steps</span>
              </Button>
              <Button
                onClick={() => setShowOwnerGuide(true)}
                variant="outline"
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 h-auto py-3 flex flex-col items-center gap-1"
              >
                <Crown className="w-5 h-5" />
                <span className="text-sm font-semibold">Owner Guide</span>
                <span className="text-xs opacity-70">8 steps</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Progress Overview */}
        <Card className="bg-stone-800/50 border-stone-700 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Setup Progress</h3>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                ~25 minutes total
              </Badge>
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {steps.map((step, idx) => (
                <div 
                  key={step.number}
                  className={`flex items-center ${idx !== steps.length - 1 ? 'flex-1' : ''}`}
                >
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer transition-all ${
                      expandedStep === step.number 
                        ? 'bg-amber-500 text-black' 
                        : 'bg-stone-700 text-gray-400 hover:bg-stone-600'
                    }`}
                    onClick={() => setExpandedStep(step.number)}
                  >
                    {step.number}
                  </div>
                  {idx !== steps.length - 1 && (
                    <div className="flex-1 h-1 bg-stone-700 mx-2 min-w-[20px]" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Steps */}
        <div className="space-y-4 mb-12">
          {steps.map((step) => (
            <Card 
              key={step.number}
              className={`bg-stone-800/50 border-stone-700 cursor-pointer transition-all ${
                expandedStep === step.number ? 'ring-2 ring-amber-500/50' : ''
              }`}
              onClick={() => setExpandedStep(expandedStep === step.number ? 0 : step.number)}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    expandedStep === step.number ? 'bg-amber-500 text-black' : 'bg-stone-700 text-gray-400'
                  }`}>
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-semibold text-lg">{step.title}</h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-gray-400 border-stone-600">
                          <Clock className="w-3 h-3 mr-1" />
                          {step.duration}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedStep === step.number ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mt-1">{step.description}</p>
                    
                    {expandedStep === step.number && (
                      <div className="mt-4 space-y-4">
                        <ul className="space-y-2">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-300">
                              <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-sm">{detail}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <p className="text-amber-400 text-sm">
                            <strong>💡 Pro Tip:</strong> {step.tip}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pro Features Highlight */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30 mb-12">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-6 h-6 text-amber-400" />
              <h3 className="text-xl font-bold text-white">Unlock Pro Features - $29/mo</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Users, text: "Unlimited clients (vs 50 on Free)" },
                { icon: Gift, text: "Loyalty rewards program" },
                { icon: Calendar, text: "Google Calendar sync" },
                { icon: CreditCard, text: "Multiple payment options" },
                { icon: Share2, text: "Social media sharing tools" },
                { icon: Star, text: "Priority listing in search" }
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <feature.icon className="w-5 h-5 text-amber-400" />
                  <span className="text-gray-300">{feature.text}</span>
                </div>
              ))}
            </div>
            <Link to="/membership">
              <Button className="mt-6 bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-semibold">
                Upgrade to Pro
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* FAQs */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="bg-stone-800/50 border-stone-700">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                  <p className="text-gray-400 text-sm">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h3 className="text-xl font-bold text-white mb-4">Ready to Get Started?</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-semibold px-8">
                Create Your Account
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/find-stylists">
              <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                See How It Looks
              </Button>
            </Link>
          </div>
          <p className="text-gray-500 text-sm mt-4">
            Questions? Email us at support@myhairchair.com
          </p>
        </div>
      </div>

      {/* Guide Modals */}
      {showClientGuide && (
        <GetStartedGuide
          userRole="client"
          onComplete={() => setShowClientGuide(false)}
          onSkip={() => setShowClientGuide(false)}
        />
      )}
      
      {showStylistGuide && (
        <GetStartedGuide
          userRole="stylist"
          onComplete={() => setShowStylistGuide(false)}
          onSkip={() => setShowStylistGuide(false)}
        />
      )}
      
      {showOwnerGuide && (
        <GetStartedGuide
          userRole="owner"
          onComplete={() => setShowOwnerGuide(false)}
          onSkip={() => setShowOwnerGuide(false)}
        />
      )}

      {/* App Introduction Video Modal */}
      <Dialog open={showIntro} onOpenChange={setShowIntro}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
          <AppIntroduction 
            onComplete={() => setShowIntro(false)}
            autoPlay={false}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingGuidePage;
